import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Lock, Unlock, Trash2 } from "lucide-react";
import ImageComposer from "./ImageComposer";

// ============ PRODUCT IMAGE MANAGER (admin ↔ Wolvox 2-way) ============
// Click a product's thumbnail in the Admin products panel → this dialog opens.
// Lets admin:
//   • Upload a new photo (phone camera or disk)
//   • Paste a direct URL
//   • Delete the image (unlock → next sync can pull from Wolvox)
//   • Toggle "Akınsoft Wolvox'a da gönder" (sets pending_push_to_wolvox).
export default function ProductImageDialog({ open, onOpenChange, product, onSaved }) {
  const fileRef = useRef(null);
  const [imageUrl, setImageUrl] = useState("");
  const [pushToWolvox, setPushToWolvox] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");
  // Composer state — kullanıcı dosya seçince composer açılır, apply edince blob upload edilir
  const [composerFile, setComposerFile] = useState(null);

  useEffect(() => {
    if (!open) return;
    setImageUrl(product?.image_url || "");
    setPreview(product?.image_url || "");
    setPushToWolvox(!product?.image_locked); // suggest push for freshly-set images
  }, [open, product]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Dosyayı doğrudan upload yerine ÖNCE composer'a yollarız
    setComposerFile(file);
  };

  const uploadBlob = async (blob) => {
    const fd = new FormData();
    fd.append("file", new File([blob], "composed.jpg", { type: "image/jpeg" }));
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) {
        setImageUrl(data.url);
        setPreview(data.url);
        toast.success("✓ Düzenlendi ve yüklendi — kaydetmeyi unutma");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yüklenemedi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onPasteUrl = (e) => {
    const v = e.target.value;
    setImageUrl(v);
    setPreview(v);
  };

  const save = async () => {
    const url = (imageUrl || "").trim();
    if (!url) {
      toast.error("Önce bir görsel yükle veya URL yapıştır");
      return;
    }
    if (!product?.id) return;
    setSaving(true);
    try {
      await api.put(`/admin/products/${product.id}/image`, {
        image_url: url,
        push_to_wolvox: pushToWolvox,
      });
      toast.success(pushToWolvox
        ? "✓ Kaydedildi · 🔒 Kilitli · 🔄 Akınsoft'a push bekliyor"
        : "✓ Kaydedildi · 🔒 Kilitli (sadece web)");
      onOpenChange(false);
      setTimeout(() => onSaved?.(), 240);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const removeImage = async () => {
    if (!product?.id) return;
    if (!window.confirm("Görsel silinsin ve kilit kaldırılsın mı? (Sonraki Wolvox sync yeni görsel getirebilir)")) return;
    setSaving(true);
    try {
      await api.delete(`/admin/products/${product.id}/image`);
      toast.success("Görsel kaldırıldı — Wolvox sync tekrar getirebilir");
      onOpenChange(false);
      setTimeout(() => onSaved?.(), 240);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinemedi");
    } finally {
      setSaving(false);
    }
  };

  const unlockOnly = async () => {
    if (!product?.id) return;
    setSaving(true);
    try {
      await api.post(`/admin/products/${product.id}/image/unlock`);
      toast.success("🔓 Kilit açıldı — sonraki Wolvox sync görsel üzerine yazabilir");
      onOpenChange(false);
      setTimeout(() => onSaved?.(), 240);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Açılamadı");
    } finally {
      setSaving(false);
    }
  };

  const sourceBadge = (() => {
    const s = product?.image_source;
    if (!s) return null;
    const map = {
      admin: { label: "🧑‍💼 Admin yükledi", cls: "bg-[#2E5A36] text-white" },
      wolvox: { label: "🏷️ Akınsoft'tan", cls: "bg-[#B4711C] text-white" },
      off: { label: "🥫 OFF'tan", cls: "bg-[#1B2A22] text-white" },
    };
    const m = map[s];
    return m ? <span className={`text-[10px] rounded-full px-2 py-0.5 font-heading ${m.cls}`}>{m.label}</span> : null;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-image-dialog" className="bg-[#FAF7F2] border-[#E8E2D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            📷 Ürün Görseli
            {product?.image_locked && (
              <span className="text-[10px] bg-[#E5A024] text-white rounded-full px-2 py-0.5 font-heading" title="Kilitli — Wolvox sync overwrite etmez">
                🔒 Kilitli
              </span>
            )}
            {product?.pending_push_to_wolvox && (
              <span className="text-[10px] bg-[#D9653B] text-white rounded-full px-2 py-0.5 font-heading animate-pulse" title="Akınsoft'a push bekliyor">
                🔄 Push Bekliyor
              </span>
            )}
            {sourceBadge}
          </DialogTitle>
          <DialogDescription className="text-xs">
            <b>{product?.name_tr}</b> · Akınsoft kod: <span className="font-mono">{product?.akinsoft_code || "—"}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Current/preview image.
              NOTE: no onError handler on the <img>. Mutating state from
              onError during a dialog-close animation caused Radix's portal
              to crash with "removeChild: not a child of this node". If the
              URL is broken, the <img> simply renders a tiny empty box — a
              worse-looking error is better than a page-killing crash. */}
          <div className="w-full aspect-square max-h-[260px] rounded-xl overflow-hidden bg-white border-2 border-dashed border-[#E8E2D5] flex items-center justify-center">
            {preview ? (
              <img
                key={preview}
                src={preview}
                alt="preview"
                data-testid="product-image-preview"
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="text-[#1B2A22]/40 text-xs">Görsel yok</span>
            )}
          </div>

          {/* Upload file */}
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFile}
              className="hidden"
              data-testid="product-image-file-input"
            />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="w-full h-11 rounded-full border-[#2E5A36] text-[#2E5A36] font-heading font-bold"
              data-testid="product-image-upload-btn"
            >
              <Upload className="w-4 h-4 mr-1" />
              {uploading ? "Yükleniyor..." : "Dosyadan Yükle / Fotoğraf Çek"}
            </Button>
          </div>

          {/* Paste URL */}
          <div>
            <Label className="text-xs">Veya URL yapıştır</Label>
            <Input
              value={imageUrl}
              onChange={onPasteUrl}
              placeholder="https://... veya /api/files/..."
              className="bg-white font-mono text-xs"
              data-testid="product-image-url-input"
            />
          </div>

          {/* Push-to-Wolvox toggle */}
          <label className="flex items-start gap-2 p-3 rounded-xl bg-[#E5A024]/10 border border-[#E5A024]/40 cursor-pointer">
            <input
              type="checkbox"
              data-testid="product-image-push-toggle"
              checked={pushToWolvox}
              onChange={(e) => setPushToWolvox(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#2E5A36]"
            />
            <div>
              <p className="text-sm font-heading font-bold text-[#B4711C]">
                🔄 Akınsoft Wolvox'a da gönder
              </p>
              <p className="text-xs text-[#1B2A22]/70 mt-1">
                İşaretli ise bir sonraki <span className="font-mono">sync.py</span> çalışmasında görsel Firebird <span className="font-mono">STOK.RESIM</span> alanına yazılır.
                İşaretli değilse sadece web'de kalır.
              </p>
            </div>
          </label>

          {/* Save + delete + unlock */}
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={save}
              disabled={saving || uploading || !imageUrl}
              className="flex-1 min-w-[160px] h-11 bg-[#2E5A36] hover:bg-[#1B2A22] text-white rounded-full font-heading font-bold"
              data-testid="product-image-save-btn"
            >
              <Lock className="w-4 h-4 mr-1" />
              {saving ? "Kaydediliyor..." : "Kaydet (Kilitle)"}
            </Button>
            {product?.image_url && (
              <Button
                onClick={removeImage}
                disabled={saving || uploading}
                variant="outline"
                className="h-11 rounded-full border-red-500 text-red-600 hover:bg-red-50"
                title="Görseli sil, kilidi kaldır → sync yeni görsel getirebilir"
                data-testid="product-image-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            {product?.image_locked && (
              <Button
                onClick={unlockOnly}
                disabled={saving || uploading}
                variant="outline"
                className="h-11 rounded-full border-[#E5A024] text-[#B4711C]"
                title="Kilidi aç ama görseli bırak — sync overwrite edebilir"
                data-testid="product-image-unlock-btn"
              >
                <Unlock className="w-4 h-4 mr-1" />
                Kilidi Aç
              </Button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Composer — dosya seçildiğinde açılır, apply edince blob upload edilir */}
      <ImageComposer
        open={!!composerFile}
        file={composerFile}
        onCancel={() => {
          setComposerFile(null);
          if (fileRef.current) fileRef.current.value = "";
        }}
        onReady={async (blob) => {
          setComposerFile(null);
          await uploadBlob(blob);
        }}
      />
    </Dialog>
  );
}
